import { useId, useMemo, useState, type CSSProperties } from "react";
import { INVALID_MIN_VERSION_TEXT } from "@klubora/shared";
import { useAuditEmails } from "../../hooks/useAuditEmails";
import {
  useMinAppCodeVersionAudit,
  type MinAppCodeVersionAudit,
} from "../../hooks/useMinAppCodeVersionAudit";
import {
  setMinAppCodeVersion,
  SetMinAppCodeVersionError,
} from "../../lib/setMinAppCodeVersion";
import { formatRelativeTime } from "../../lib/relativeTime";
import { parseMinVersionDraft } from "../../lib/minAppCodeVersionDraft";
import { ConfirmDialog } from "../ConfirmDialog";
import { EditorFooter } from "./EditorFooter";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../../lib/tokens";

interface Props {
  tenantId: string;
  tenantName: string;
  /** `tenants.min_app_code_version` as last read. `null` = no minimum. */
  initialValue: number | null;
}

/**
 * KLUB-245 U3 — "Versão do app" group on the tenant's Aplicativo tab.
 *
 * Not part of `TenantSettingsEditor`: the value lives in `tenants`, not in
 * `tenant_settings`, and is written by its own edge function, so the group has
 * its own Cancelar/Salvar row. Saving always goes through a confirmation that
 * shows the current value, the new one and the lock-out warning — that dialog
 * is the only guard against a mistyped number (the function has no ceiling).
 */
export function MinAppCodeVersionField({ tenantId, tenantName, initialValue }: Props) {
  const [saved, setSaved] = useState<number | null>(initialValue);
  const [draft, setDraft] = useState<string>(initialValue === null ? "" : String(initialValue));
  const [pending, setPending] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditReload, setAuditReload] = useState(0);

  const ids = useId();
  const footnoteId = `${ids}-footnote`;
  const hintId = `${ids}-hint`;
  const audit = useMinAppCodeVersionAudit(tenantId, auditReload);
  const auditUserIds = useMemo(
    () => (audit?.actorUserId ? [audit.actorUserId] : []),
    [audit],
  );
  const emails = useAuditEmails(auditUserIds);

  const savedText = saved === null ? "" : String(saved);
  const dirty = draft.trim() !== savedText;
  const invalid = error === INVALID_MIN_VERSION_TEXT;

  const commitSaved = (value: number | null) => {
    setSaved(value);
    setDraft(value === null ? "" : String(value));
  };

  const handleChange = (next: string) => {
    setDraft(next);
    setError(null);
  };

  const handleCancel = () => {
    setDraft(savedText);
    setError(null);
  };

  const handleSave = () => {
    const parsed = parseMinVersionDraft(draft);
    if (!parsed.ok) {
      setError(INVALID_MIN_VERSION_TEXT);
      return;
    }
    setError(null);
    if (parsed.value === saved) {
      // "07" over 7, or padded blanks: nothing would change — normalise, no dialog.
      commitSaved(saved);
      return;
    }
    setPending(parsed.value);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await setMinAppCodeVersion(tenantId, pending);
      commitSaved(res.min_app_code_version);
    } catch (e) {
      if (e instanceof SetMinAppCodeVersionError && e.persisted !== undefined) {
        commitSaved(e.persisted);
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
      setConfirmOpen(false);
      setAuditReload((k) => k + 1);
    }
  };

  const footnote = footnoteText(saved, audit, emails);

  return (
    <section style={s.section}>
      <h3 style={s.heading}>Versão do app</h3>
      <div style={s.card}>
        <label style={s.label}>
          <span style={s.labelText}>Atualização mínima</span>
          <input
            type="text"
            inputMode="numeric"
            value={draft}
            placeholder="Sem mínimo"
            disabled={saving}
            aria-invalid={invalid}
            aria-describedby={footnote ? `${footnoteId} ${hintId}` : hintId}
            onChange={(e) => handleChange(e.target.value)}
            style={{ ...s.input, border: `1px solid ${invalid ? colors.error : colors.fog}` }}
          />
        </label>
        {footnote && <span id={footnoteId} style={s.footnote}>{footnote}</span>}
        <p id={hintId} style={s.hint}>
          Aparelhos que ainda não receberam esta atualização veem &quot;Atualize para continuar&quot;.
          Deixe vazio para não bloquear ninguém.
        </p>
        <div style={s.footer}>
          <EditorFooter
            dirty={dirty}
            saving={saving}
            error={error}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={pending === null ? "Remover a atualização mínima?" : `Exigir a atualização ${pending}?`}
        message={confirmMessage(saved, pending, tenantName)}
        confirmLabel={pending === null ? "Remover mínimo" : `Definir ${pending}`}
        cancelLabel="Cancelar"
        loading={saving}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </section>
  );
}

function confirmMessage(current: number | null, next: number | null, tenantName: string) {
  const hoje = current === null ? "sem mínimo" : String(current);
  if (next === null) {
    return (
      <>
        Hoje: {hoje}. Novo: sem mínimo.
        <br />
        Nenhum aparelho do {tenantName} fica mais bloqueado por atualização. Quem estava
        bloqueado volta a entrar ao reabrir o app.
      </>
    );
  }
  return (
    <>
      Hoje: {hoje}. Novo: {next}.
      <br />
      Todo aparelho do {tenantName} que ainda não recebeu a atualização {next} fica bloqueado
      até atualizar, antes e depois do login.
    </>
  );
}

function footnoteText(
  saved: number | null,
  audit: MinAppCodeVersionAudit | null,
  emails: Record<string, string>,
): string | null {
  const parts: string[] = [];
  if (saved === null) parts.push("nenhum aparelho é bloqueado");
  if (audit) {
    const who = audit.actorUserId ? emails[audit.actorUserId] ?? null : null;
    const when = formatRelativeTime(audit.createdAt);
    parts.push(who ? `editado por ${who} ${when}` : `editado ${when}`);
    parts.push(audit.from === null ? "era sem mínimo" : `era ${audit.from}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

const s: Record<string, CSSProperties> = {
  section: { marginBottom: space.xxl },
  heading: {
    fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, marginBottom: space.md,
  },
  card: {
    background: colors.white, border: `1px solid ${colors.fog}`,
    borderRadius: radius.md, boxShadow: shadow.sm, padding: space.lg,
  },
  label: { display: "block" },
  labelText: {
    display: "block", fontSize: fontSize.sm, fontWeight: fontWeight.medium,
    color: colors.stone, marginBottom: 6,
  },
  input: {
    width: 160, height: 36, padding: "0 12px",
    background: colors.white,
    borderRadius: radius.sm, fontSize: fontSize.md, color: colors.ink,
    fontFamily: "inherit", outline: "none",
  },
  footnote: { display: "block", fontSize: fontSize.sm, color: colors.stoneSoft, marginTop: space.xs },
  hint: { fontSize: fontSize.sm, color: colors.stone, lineHeight: 1.5, margin: `${space.sm}px 0 0` },
  footer: { marginTop: space.md },
};
